// Brand Graph Builder — Phase 2 UI.
//
// Two-pane workspace. Left (420px): chat pane bound to the
// graph_builder agent (POST /graph/chat SSE). Right: graph view with
// tabs per category + a History tab. Shares layout primitives with the
// briefing workspace but uses a cooler slate accent rail and a
// distinct top strip so users know they're in a different mode.

import { useCallback, useEffect, useMemo, useState } from "react";

import { BrandAtlas, type AtlasTab } from "@/components/BrandAtlas";
import { GraphChatPane } from "@/components/GraphChatPane";
import { api } from "@/lib/api";
import { useStore } from "@/lib/store";
import type {
  GraphEditEntry,
  GraphProposal,
  GraphSnapshot,
} from "@/lib/types";

/** Shared spotlight state — the agent's current focus area on the atlas.
 *  Populated by GraphChatPane from streaming tool_use events, consumed
 *  by BrandAtlas to dim everything except the focused targets.
 *  Empty arrays = no focus (everything renders at normal brightness). */
export interface AtlasFocus {
  /** Live graph node ids the agent is touching. */
  nodeIds: string[];
  /** Server category names the agent is touching (e.g. "ToneOfVoice").
   *  BrandAtlas maps these to atlas-internal category ids. */
  serverCategories: string[];
}

const EMPTY_FOCUS: AtlasFocus = { nodeIds: [], serverCategories: [] };

// Default tenant for first-time visitors. Real tenant id is persisted
// to localStorage so a tab refresh keeps the current brand.
const DEFAULT_TENANT_ID = "100";

// One graph-builder session id per browser session. Kept in
// localStorage so a tab refresh resumes the same SDK session.
// Keyed by tenant — switching brands gives the new brand its own
// fresh chat session, no cross-brand turn history bleed.
const SESSION_STORAGE_KEY_PREFIX = "inku.graph_session_id.";
const TENANT_STORAGE_KEY = "inku.graph_tenant_id";
const MODE_OVERRIDE_KEY = "inku.graph_mode_override";

function loadTenantId(): string {
  const v = (localStorage.getItem(TENANT_STORAGE_KEY) || "").trim();
  return v || DEFAULT_TENANT_ID;
}

function getOrCreateSessionId(tenantId: string): string {
  const key = SESSION_STORAGE_KEY_PREFIX + tenantId;
  const existing = localStorage.getItem(key);
  if (existing) return existing;
  const fresh = "graph_" + Math.random().toString(36).slice(2, 12);
  localStorage.setItem(key, fresh);
  return fresh;
}

export type ModeOverride = "onboarding" | "living" | "auto";

function loadModeOverride(): ModeOverride {
  const v = (localStorage.getItem(MODE_OVERRIDE_KEY) || "").toLowerCase();
  if (v === "onboarding" || v === "living") return v;
  return "auto";
}

export function GraphBuilderPage() {
  const { setToast } = useStore();

  const [tenantId, setTenantIdState] = useState<string>(loadTenantId);
  const [sessionId, setSessionId] = useState<string>(() =>
    getOrCreateSessionId(loadTenantId())
  );

  // Switch tenants: persist the new id, reset session storage so the
  // chat starts fresh, swap state. The backend auto-creates the Brand
  // root on first snapshot fetch so no extra API call needed here.
  const onChangeTenant = useCallback((next: string) => {
    const trimmed = next.trim();
    if (!trimmed) return;
    localStorage.setItem(TENANT_STORAGE_KEY, trimmed);
    setTenantIdState(trimmed);
    setSessionId(getOrCreateSessionId(trimmed));
  }, []);

  const [snapshot, setSnapshot] = useState<GraphSnapshot | null>(null);
  const [, setEdits] = useState<GraphEditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [modeOverride, setModeOverride] = useState<ModeOverride>(
    loadModeOverride
  );
  const [atlasTab, setAtlasTab] = useState<AtlasTab>("explore");
  // Live spotlight state — updated by GraphChatPane on each tool_use
  // event the agent fires. Cleared 4s after the stream ends so the
  // atlas relaxes back to a neutral view.
  const [focus, setFocus] = useState<AtlasFocus>(EMPTY_FOCUS);

  const setModeOverridePersisted = useCallback((next: ModeOverride) => {
    setModeOverride(next);
    if (next === "auto") {
      localStorage.removeItem(MODE_OVERRIDE_KEY);
    } else {
      localStorage.setItem(MODE_OVERRIDE_KEY, next);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [tree, history] = await Promise.all([
        api.graph.getTree({ tenantId }),
        api.graph.getHistory({ tenantId, limit: 100 }),
      ]);
      setSnapshot(tree);
      setEdits(history.edits);
    } catch (err) {
      setToast({
        kind: "error",
        text:
          err instanceof Error
            ? err.message
            : "Failed to load graph state.",
      });
    } finally {
      setLoading(false);
    }
  }, [tenantId, setToast]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const onProposalStaged = useCallback((_proposal: GraphProposal) => {
    // Future: surface "pending: N" badge somewhere. For now, the diff
    // card in the chat is enough.
  }, []);

  // What we actually send to the server. "auto" → undefined, which
  // lets the server infer mode from node count (the default behavior).
  const effectiveSessionMode: "onboarding" | "living" | undefined =
    modeOverride === "auto" ? undefined : modeOverride;

  // What we render as the "current mode" pill. When auto-inferred,
  // derive from the live snapshot's total node count (matches the
  // server's threshold of <10 = onboarding).
  const inferredMode: "onboarding" | "living" = useMemo(() => {
    const total = Object.values(snapshot?.counts ?? {}).reduce(
      (a, b) => a + b,
      0
    );
    return total < 10 ? "onboarding" : "living";
  }, [snapshot]);
  const displayMode =
    modeOverride === "auto" ? inferredMode : modeOverride;

  return (
    <div className="h-[calc(100vh-56px)] flex bg-background">
      {/* Left: chat */}
      <div className="w-[440px] shrink-0 border-r border-border flex flex-col">
        <GraphChatPane
          // Key on sessionId so switching tenants forces a clean remount —
          // the pane's localStorage-backed turn history is initialized once
          // via useState and would otherwise stick to the previous tenant.
          key={sessionId}
          sessionId={sessionId}
          tenantId={tenantId}
          sessionMode={effectiveSessionMode}
          snapshot={snapshot}
          onProposalResolved={refresh}
          onProposalStaged={onProposalStaged}
          onFocusChange={setFocus}
        />
      </div>

      {/* Right: brand atlas (radial visualization) */}
      <div className="flex-1 min-w-0 flex flex-col">
        <BrandAtlas
          snapshot={snapshot}
          tenantId={tenantId}
          mode={displayMode}
          modeOverride={modeOverride}
          onSetModeOverride={setModeOverridePersisted}
          tab={atlasTab}
          onSetTab={setAtlasTab}
          loading={loading}
          onRefresh={() => void refresh()}
          onChangeTenant={onChangeTenant}
          focus={focus}
        />
      </div>
    </div>
  );
}
